/* ==========================================================================
   T3 hero slider
   Deliberately boring: crossfade only, one timer, never touches scroll.

   The three things that made the old Wix slider jerk, and how each is avoided:
     1. Slides changing the container height  -> every slide sits in the SAME
        css-grid cell, so the box is always as tall as the tallest slide.
     2. Leaked timers making slides flip early -> exactly one handle on the
        instance, cleared before it is ever re-assigned.
     3. Scroll position being restored/clamped -> this file never reads or
        writes window.scrollY.
   ========================================================================== */

// Declared inside the guard, not at the top level: if this file is ever
// requested twice, a top-level class would throw "already been declared", and
// Shopify's asset minifier unwraps an IIFE so that is not a fix.
if (!customElements.get('t3-hero-slider')) {
  class T3HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.index = 0;
    this.paused = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-t3-slide]'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('[data-t3-dot]'));
    this.prevBtn = this.querySelector('[data-t3-prev]');
    this.nextBtn = this.querySelector('[data-t3-next]');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
    this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 6000);

    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.index < 0) this.index = 0;
    this.applyState();

    if (this.slides.length > 1) {
      this.bindControls();
      this.bindSwipe();
      this.bindPause();
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* ---------------------------------------------------------------- state */

  applyState() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      // Keep hidden slides out of the tab order and off the a11y tree.
      slide.inert = !active;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  goTo(next) {
    const count = this.slides.length;
    if (count === 0) return;
    this.index = ((next % count) + count) % count;
    this.applyState();
  }

  next() {
    this.goTo(this.index + 1);
  }

  prev() {
    this.goTo(this.index - 1);
  }

  /* ---------------------------------------------------------------- timer */

  // One handle, always cleared first. Never create a second interval.
  start() {
    this.stop();
    if (!this.autoplay || this.paused || this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Any manual interaction restarts the clock so the slide does not flip
  // immediately after the visitor moved it themselves.
  restart() {
    this.start();
  }

  /* ------------------------------------------------------------- controls */

  bindControls() {
    this.prevBtn?.addEventListener('click', () => {
      this.prev();
      this.restart();
    });

    this.nextBtn?.addEventListener('click', () => {
      this.next();
      this.restart();
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(parseInt(dot.dataset.t3Dot, 10) || 0);
        this.restart();
      });
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
        this.restart();
      } else if (e.key === 'ArrowRight') {
        this.next();
        this.restart();
      }
    });
  }

  // No hover/focus pause (client's call): on phones a tap fires mouseenter and
  // focusin with no matching leave, so the slider stayed paused after a touch.
  bindPause() {
    // A background tab must not queue up a burst of slide changes.
    this.onVisibility = () => {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /* ---------------------------------------------------------------- swipe */

  bindSwipe() {
    const viewport = this.querySelector('[data-t3-viewport]');
    if (!viewport) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    viewport.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX;
        startY = e.clientY;
        tracking = true;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'pointerup',
      (e) => {
        if (!tracking) return;
        tracking = false;

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        // Horizontal intent only. A mostly-vertical drag is the visitor
        // scrolling the page and must be left completely alone.
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

        if (dx < 0) this.next();
        else this.prev();
        this.restart();
      },
      { passive: true }
    );

    viewport.addEventListener('pointercancel', () => {
      tracking = false;
    });
  }
}

  customElements.define('t3-hero-slider', T3HeroSlider);
}
